/**
 * View model for the Brand Updates 2026 beta home page.
 *
 * Design source: Claude Design project 9190654a-6db9-428c-b7ef-5cbb8d5ee149,
 * file `Home Page Concepts.dc.html`.
 *
 * This module is the ONLY place that knows how the design's fields map onto
 * live TIO data. It is deliberately a pure function of its inputs — no service
 * injection, no fetching — so the mapping is unit-testable and every data gap
 * is visible in one file rather than scattered across templates.
 *
 * Each derived figure carries a `source` of 'live' | 'demo' | 'placeholder'.
 * 'placeholder' means the design asks for a number the backend cannot answer
 * yet; those are enumerated in `app/components/beta/BETA_HOME_GAPS.md`.
 */
import { formatCurrency } from 'tio-ui/format';
import {
  PhFileText,
  PhFirstAidKit,
  PhGift,
  PhGlobe,
  PhGraduationCap,
  PhHandshake,
  PhHeadset,
  PhRocketLaunch,
  PhScales,
  PhSealQuestion,
  PhShieldCheck,
  PhSpinnerGap,
  PhTarget,
  PhTrendUp,
  PhBookOpen,
  PhDeviceMobile,
} from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';
import type {
  ContributionActionItem,
  DataSourceType,
  LiveTasApplication,
} from 'tio-employee/services/reimagined-data';
import {
  EVIDENCE_ACTIONABLE_STATES,
  EVIDENCE_RETURNED_STATES,
} from 'tio-employee/utils/tas/attention-tasks';
import type { PhoneVerificationState } from 'tio-data/data/resources/phone-number.type';
import type { ReimaginedHomeModel } from 'tio-employee/routes/authenticated/reimagined/home';
import { MANAGE_ROUTE } from 'tio-employee/utils/tas/manage-link';

export type BetaIcon = ComponentLike<{ Args: { size: number }; Element: SVGElement }>;

/** Which of the design's two user states to render. */
export type BetaHomeAudience = 'new' | 'active';

/** The design defines exactly two alert tones; see `alertToneFor`. */
export type BetaAlertTone = 'danger' | 'warning';

export interface BetaStat {
  id: string;
  label: string;
  value: string;
  meta: string;
  icon: BetaIcon;
  source: DataSourceType;
}

/** A row that opens something in place instead of navigating. */
export type BetaAlertAction = 'verify-phone';

export interface BetaAlert {
  id: string;
  tone: BetaAlertTone;
  badge: string;
  title: string;
  description: string;
  cta: string;
  /** Exactly one of `route` or `action` is set. */
  route?: string;
  action?: BetaAlertAction;
  /** Dynamic segments for `route`, when it takes any. */
  models?: string[];
  icon: BetaIcon;
}

export interface BetaProgressCard {
  id: string;
  title: string;
  icon: BetaIcon;
  linkLabel: string;
  route: string;
  value: string;
  caption: string;
  /** 0–100, already clamped. */
  percent: number;
  meta: string;
  metaIcon: BetaIcon;
  source: DataSourceType;
}

export interface BetaDiscoverCard {
  id: string;
  title: string;
  description: string;
  /** Design calls this the card's "meta" line; omitted on Resources cards. */
  meta?: string;
  icon: BetaIcon;
  route?: string;
  href?: string;
}

export interface BetaHomeModel {
  audience: BetaHomeAudience;
  greeting: string;
  firstName: string;
  /** New-user hero only. */
  availableTuition: string;
  availableTuitionSource: DataSourceType;
  stats: BetaStat[];
  alerts: BetaAlert[];
  progressCards: BetaProgressCard[];
  /** "Explore Your Benefits" (new) / "More to Explore" (active). */
  discover: BetaDiscoverCard[];
  resources: BetaDiscoverCard[];
}

/**
 * Every benefit on. The default for `audienceFor` so its existing callers keep
 * their behaviour; `buildBetaHomeModel` always passes the real flags.
 */
const ALL_BENEFITS_ON: BetaHomeFlags = {
  showPhoneCapture: true,
  showTuitionAssistance: true,
  showStudentLoanRepayment: true,
  showPslf: true,
  showFinancialWellness: true,
  showLpn: true,
  showMentoring: true,
  showSyf: true,
  showIdr: true,
};

/** Partner/plan switches that gate which cards appear. */
export interface BetaHomeFlags {
  showTuitionAssistance: boolean;
  showStudentLoanRepayment: boolean;
  showPslf: boolean;
  /** Its own entitlement (`partner.hasWellness`), not a loan sub-benefit. */
  showFinancialWellness: boolean;
  showLpn: boolean;
  showMentoring: boolean;
  showSyf: boolean;
  showIdr: boolean;
  showPhoneCapture: boolean;
}

export interface BetaHomeInput {
  model: ReimaginedHomeModel;
  firstName: string;
  /** Hour of day, injected so the greeting is testable. */
  hour: number;
  /**
   * Server-driven loan-repayment action items, from
   * `reimaginedData.fetchContributionHistory().actionItems`. These already carry
   * their own badge copy and a danger/warning/default intent, so they need no
   * client-side severity guessing.
   */
  actionItems: ContributionActionItem[];
  flags: BetaHomeFlags;
  /**
   * The mobile-number row's inputs. Copy is resolved by the caller: this builder is a
   * pure function with no container, and its unit tests rely on that.
   */
  phoneCapture?: {
    state: PhoneVerificationState;
    title: string;
    description: string;
    cta: string;
  };
  /**
   * Tuition assistance spent in the current benefit year.
   *
   * GAP (ta-ytd): `LiveTasData.summary` exposes lifetime `totalPaid` but no
   * benefit-year split, so the caller supplies this from
   * `reimaginedData.fetchTasUsedAmount()`. Pass `null` when it could not be
   * resolved — the stat then renders em-dash rather than a wrong zero.
   */
  tuitionYearToDate: number | null;
}

const PSLF_REQUIRED_PAYMENTS = 120;

export function greetingFor(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

/** The design's two badge labels. */
const BADGE_ACT_NOW = 'Act Now';
const BADGE_DUE_SOON = 'Due Soon';

/**
 * TAS states where the employee owes an action, taken from the canonical set in
 * `utils/tas/attention-tasks.ts` rather than re-listed here.
 *
 * That module derives the exact per-task list (use CourseCash, upload receipt,
 * submit evidence) with due dates and urgency for ONE application's manage
 * screen, and needs the full application model plus CourseCash transactions and
 * program template. This is a dashboard roll-up over `LiveTasApplication` — a
 * coarse "this application needs you" signal that links INTO the manage screen.
 * Importing its state set keeps the two from disagreeing about WHICH
 * applications need attention, while leaving the per-task detail where it
 * belongs.
 *
 * `EVIDENCE_ACTIONABLE_STATES` deliberately excludes PENDING_COURSES_APPROVAL
 * and PENDING_EVIDENCE_APPROVAL — those wait on an approver, so surfacing them
 * would tell people to act when there is nothing they can do.
 */
const TAS_EVIDENCE_DUE_STATES = new Set(
  [...EVIDENCE_ACTIONABLE_STATES].filter((state) => !EVIDENCE_RETURNED_STATES.has(state))
);

const TAS_DRAFT_STATES = new Set([
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.DEFINE_COURSES',
]);

/** Whole dollars — the design shows no cents anywhere. */
function money(amount: number): string {
  return formatCurrency(amount, 0);
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, Math.round(value)));
}

/**
 * Highest qualifying-payment count across the person's loans.
 *
 * Mirrors the shipped beta dashboard's derivation rather than inventing a
 * second one: PSLF forgiveness tracks the furthest-along loan.
 */
export function pslfQualifyingPayments(model: ReimaginedHomeModel): number {
  const counts = model.pslfData.accounts.flatMap((account) =>
    account.loans.map((loan) => loan.qualifyingPayments ?? 0)
  );
  return counts.length ? Math.max(...counts) : 0;
}

/**
 * Whether the person has any recorded benefit activity.
 *
 * Drives the design's New/Active split. Deliberately generous: any employer
 * money received, any TA program or application, or any PSLF form on file
 * counts as active, because the "Welcome" hero reads wrong to someone who has
 * already transacted.
 */
export function audienceFor(
  model: ReimaginedHomeModel,
  flags: BetaHomeFlags = ALL_BENEFITS_ON
): BetaHomeAudience {
  const { employerContributions, tasData, pslfData } = model;

  // Only activity in a benefit the partner OFFERS can make someone an active
  // user. Otherwise leftover data in a disabled benefit — a ta-only partner
  // whose contribution history predates the plan change — renders the
  // active-user hero for a benefit whose cards and nav entry are all hidden.
  const hasEmployerMoney =
    flags.showStudentLoanRepayment && employerContributions.contributions.lifetime > 0;
  const hasTas =
    flags.showTuitionAssistance &&
    (tasData.programs.length > 0 ||
      tasData.applications.length > 0 ||
      tasData.summary.activeCount > 0);
  const hasPslf = flags.showPslf && pslfData.forms.length > 0;

  return hasEmployerMoney || hasTas || hasPslf ? 'active' : 'new';
}

/**
 * "Active Benefit Programs" — the count behind the fourth KPI.
 *
 * Counts distinct TA program instances in flight, NOT applications.
 * `summary.activeCount` is an application count, so someone with three active
 * course requests inside one degree program would read as three "programs".
 * Applications without a programInstanceId fall back to counting once each,
 * since there is nothing to group them by.
 */
function activeProgramCount(model: ReimaginedHomeModel, flags: BetaHomeFlags): number {
  const activePrograms = new Set<string>();
  let ungrouped = 0;

  for (const application of model.tasData.applications) {
    if (!TAS_ACTIVE_STATES.has(application.state)) continue;
    if (application.programInstanceId) {
      activePrograms.add(application.programInstanceId);
    } else {
      ungrouped += 1;
    }
  }

  const tas = flags.showTuitionAssistance ? activePrograms.size + ungrouped : 0;
  const pslf = flags.showPslf && model.pslfData.forms.length > 0 ? 1 : 0;
  return tas + pslf;
}

/**
 * States that count as a program being in flight. Mirrors the `activeStates` set
 * `reimagined-data.ts` uses to build `summary.activeCount`, so the KPI's notion
 * of "active" matches the summary's even though it counts a different unit.
 */
const TAS_ACTIVE_STATES = new Set([
  'TAS.ApplicationState.ATTEND',
  'TAS.ApplicationState.COURSES_APPROVED',
]);

function buildStats(input: BetaHomeInput): BetaStat[] {
  const { model, tuitionYearToDate, flags } = input;
  const { contributions, isLiveData } = model.employerContributions;
  const contributionSource: DataSourceType = isLiveData ? 'live' : 'demo';
  const tasSource: DataSourceType = model.tasData.isLiveData ? 'live' : 'demo';

  // Only count money from benefits the partner actually offers, so a no-TA or
  // no-SLR partner's "Total Employer Investment" is not inflated by a benefit
  // they do not have.
  const tuitionLifetime = flags.showTuitionAssistance ? model.tasData.summary.totalPaid : 0;
  const tuitionYtd = flags.showTuitionAssistance ? tuitionYearToDate : 0;
  const contributionLifetime = flags.showStudentLoanRepayment ? contributions.lifetime : 0;
  const contributionYtd = flags.showStudentLoanRepayment ? contributions.yearToDate : 0;
  const payments = pslfQualifyingPayments(model);

  /**
   * GAP (employer-investment): the design's "Total Employer Investment" is a
   * roll-up with no single backend field. Read here as loan-repayment
   * contributions plus tuition assistance actually paid — consistent with the
   * design's own arithmetic, where employer investment exceeds the tuition
   * figure by the contribution total. Confirm with product before GA.
   */
  const investmentLifetime = contributionLifetime + tuitionLifetime;
  const investmentYtd = tuitionYtd === null ? null : contributionYtd + tuitionYtd;

  const stats: BetaStat[] = [
    {
      id: 'employer-investment',
      label: 'Total Employer Investment',
      value: money(investmentLifetime),
      meta: investmentYtd === null ? '— year to date' : `${money(investmentYtd)} year to date`,
      icon: PhHandshake,
      source: contributionSource,
    },
  ];

  // Each remaining KPI is gated on the partner offering that benefit — otherwise
  // a no-TA or no-PSLF partner sees a zero-valued card for something they cannot
  // use, which the progress and discover grids already correctly hide.
  if (flags.showTuitionAssistance) {
    stats.push({
      id: 'tuition-assistance',
      label: 'Total Tuition Assistance',
      value: money(tuitionLifetime),
      meta: tuitionYtd === null ? '— year to date' : `${money(tuitionYtd)} year to date`,
      icon: PhGraduationCap,
      source: tasSource,
    });
  }

  // Enrolment, not entitlement: 0/120 for someone with no PSLF form is not progress.
  if (flags.showPslf && model.pslfData.forms.length > 0) {
    stats.push({
      id: 'forgiveness-progress',
      label: 'Loan Forgiveness Progress',
      value: `${clampPercent((payments / PSLF_REQUIRED_PAYMENTS) * 100)}%`,
      meta: `${payments}/${PSLF_REQUIRED_PAYMENTS} payments made`,
      icon: PhTrendUp,
      source: model.pslfData.isLiveData ? 'live' : 'demo',
    });
  }

  stats.push({
    id: 'active-programs',
    label: 'Active Benefit Programs',
    value: String(activeProgramCount(model, flags)),
    meta: 'currently in progress',
    icon: PhSpinnerGap,
    source: tasSource,
  });

  return stats;
}

/** One TAS application → an attention row, or null when nothing is owed. */
function tasAlertFor(application: LiveTasApplication): BetaAlert | null {
  // An instance row's id is a program-instance id; MANAGE_ROUTE resolves a
  // tas-application, so every row below would 404 on one.
  if (application.recordType !== 'application') return null;

  const label = application.programName ?? application.name;

  // Evidence came back — both EVIDENCE_REJECTED and EVIDENCE_NOT_APPROVED.
  if (EVIDENCE_RETURNED_STATES.has(application.state)) {
    return {
      id: `tas-evidence-returned-${application.id}`,
      tone: 'danger',
      badge: BADGE_ACT_NOW,
      title: 'Your Education Request Needs Attention',
      description: `${label} — your evidence was not approved. Review the feedback and resubmit.`,
      cta: 'Submit Evidence',
      route: MANAGE_ROUTE,
      models: [application.id],
      icon: PhGraduationCap,
    };
  }

  if (TAS_EVIDENCE_DUE_STATES.has(application.state)) {
    return {
      id: `tas-evidence-due-${application.id}`,
      tone: 'warning',
      badge: BADGE_DUE_SOON,
      title: 'Your Education Request Needs Attention',
      description: 'Submit your evidence items to keep the process moving smoothly.',
      cta: 'Submit Evidence',
      route: MANAGE_ROUTE,
      models: [application.id],
      icon: PhGraduationCap,
    };
  }

  if (TAS_DRAFT_STATES.has(application.state)) {
    return {
      id: `tas-draft-${application.id}`,
      tone: 'danger',
      badge: BADGE_ACT_NOW,
      title: 'Finish Your Education Request',
      description: `${label} is still a draft. Complete it so it can be reviewed.`,
      cta: 'Finish Request',
      route: MANAGE_ROUTE,
      models: [application.id],
      icon: PhGraduationCap,
    };
  }

  // Last, not first: money owed outlives the state machine, but where an
  // application is also actionable the row the employee can act on wins.
  if (application.inRepayment) {
    return {
      id: `tas-in-repayment-${application.id}`,
      tone: 'danger',
      badge: BADGE_ACT_NOW,
      title: 'You Have a Balance to Repay',
      description: `${label} — a balance is outstanding on this request. Review the details.`,
      cta: 'Review Details',
      route: MANAGE_ROUTE,
      models: [application.id],
      icon: PhScales,
    };
  }

  return null;
}

/**
 * "Needs Your Attention" rows, highest urgency first.
 *
 * Three sources, all real state — no invented urgency:
 *   1. loan-repayment action items from /slr-alerts (server-authored copy)
 *   2. TAS applications where the employee owes an action
 *   3. the annual PSLF certification, when a form is on file
 *
 * Opportunities ("you have unused benefits", "here's a better split") are
 * deliberately NOT here — the design represents those as "More to Explore"
 * cards, so putting them in this panel would double-count them.
 */
function buildAlerts(input: BetaHomeInput): BetaAlert[] {
  const alerts: BetaAlert[] = [];

  // Every one of these routes to authenticated.slr.dashboard, so a partner
  // without loan repayment would get attention rows pointing at a benefit they
  // do not have — and the nav rail correctly hides that destination.
  for (const item of input.flags.showStudentLoanRepayment ? input.actionItems : []) {
    alerts.push({
      id: `slr-${item.id}`,
      tone: item.badgeIntent === 'danger' ? 'danger' : 'warning',
      // The API authors this copy; fall back only if it arrives empty.
      badge: item.badge || (item.badgeIntent === 'danger' ? BADGE_ACT_NOW : BADGE_DUE_SOON),
      title: item.title,
      description: item.description,
      cta: 'Resolve Now',
      /**
       * Deliberately NOT `item.route`. Those routes were authored for the alpha
       * surface — `authenticated.reimagined.loans` for dropdown resolutions, and
       * `authenticated.observability.upload` for uploads, which needs `source` /
       * `providers` query params this roll-up has no way to supply. Sending beta
       * users to an alpha route, or to a resolver missing its params, both land
       * somewhere useless.
       *
       * The real SLR dashboard renders these same action items with their
       * resolution controls attached, so it resolves every variant without
       * parameters.
       */
      route: 'authenticated.slr.dashboard',
      icon: PhHandshake,
    });
  }

  for (const application of input.model.tasData.applications) {
    const alert = tasAlertFor(application);
    if (alert) alerts.push(alert);
  }

  /**
   * Only when a form is actually near recertification. `isExpiring` is the model's
   * own definition of that — submitted or pending, and old enough to be inside the
   * recert warning window. Gating on `forms.length` alone told someone who
   * certified last week to certify again.
   */
  if (input.flags.showPslf && input.model.pslfData.forms.some((form) => form.isExpiring)) {
    const payments = pslfQualifyingPayments(input.model);
    const remaining = PSLF_REQUIRED_PAYMENTS - payments;
    alerts.push({
      id: 'pslf-certification',
      // Inside the last year of payments, certifying late costs real months.
      tone: remaining <= 12 ? 'danger' : 'warning',
      badge: remaining <= 12 ? BADGE_ACT_NOW : BADGE_DUE_SOON,
      title: 'Annual PSLF Certification Due Soon',
      description: 'Certify your qualifying payments to stay on track for forgiveness',
      cta: 'Certify Now',
      route: 'authenticated.pslf.index',
      icon: PhFileText,
    });
  }

  /**
   * Permanent until a number is verified: no dismissal, no cadence. `unverified` is
   * included because the server refuses the overlay once a number exists, so this row
   * is the only proactive route left to a member who stopped partway.
   */
  if (
    input.flags.showPhoneCapture &&
    (input.phoneCapture?.state === 'none' || input.phoneCapture?.state === 'unverified')
  ) {
    alerts.push({
      id: 'phone-capture',
      tone: 'warning',
      badge: BADGE_DUE_SOON,
      title: input.phoneCapture.title,
      description: input.phoneCapture.description,
      cta: input.phoneCapture.cta,
      action: 'verify-phone',
      icon: PhDeviceMobile,
    });
  }

  return alerts.sort((a, b) => toneRank(a.tone) - toneRank(b.tone));
}

function toneRank(tone: BetaAlertTone): number {
  return tone === 'danger' ? 0 : 1;
}

function buildProgressCards(input: BetaHomeInput): BetaProgressCard[] {
  const { model, flags, tuitionYearToDate } = input;
  const cards: BetaProgressCard[] = [];

  if (flags.showTuitionAssistance) {
    const limit = model.section127Info.annualLimit;
    const used = tuitionYearToDate ?? model.section127Info.yearToDateUsed;
    cards.push({
      id: 'tuition-assistance',
      title: 'Tuition Assistance',
      icon: PhGraduationCap,
      linkLabel: 'View Activity',
      route: 'authenticated.tas.dashboard',
      value: money(used),
      /**
       * GAP (ta-benefit-year): the design's caption is "Used This Benefit Year",
       * but the only available figure is neither. `fetchTasUsedAmount` filters on
       * `createdAt.getFullYear()` — the CALENDAR year, while this workspace's
       * benefit year starts in October (`utils/benefit-year.ts`) — and sums
       * `approvedTotal`, so approved-but-unpaid counts as used. Labelled for what
       * the number actually is rather than printing a benefit-year claim over
       * calendar-year approved totals.
       */
      caption: 'Approved This Year',
      percent: limit > 0 ? clampPercent((used / limit) * 100) : 0,
      meta: `eligible up to ${money(limit)}`,
      metaIcon: PhGift,
      source: model.section127Info.isLiveData ? 'live' : 'demo',
    });
  }

  if (flags.showStudentLoanRepayment) {
    const { contributions } = model.employerContributions;
    // Against the projected annual contribution, not the Section 127 cap: that
    // cap is shared with TA, so using it would have both cards claiming the
    // same denominator.
    const projected = contributions.projectedAnnual;
    cards.push({
      id: 'student-loan-repayment',
      title: 'Loan Repayment',
      icon: PhHandshake,
      linkLabel: 'View Activity',
      route: 'authenticated.slr.dashboard',
      value: money(contributions.yearToDate),
      caption: 'Paid This Year',
      percent: projected > 0 ? clampPercent((contributions.yearToDate / projected) * 100) : 0,
      meta: `${money(contributions.lifetime)} lifetime`,
      metaIcon: PhGift,
      source: model.employerContributions.isLiveData ? 'live' : 'demo',
    });
  }

  if (flags.showPslf && model.pslfData.forms.length > 0) {
    const payments = pslfQualifyingPayments(model);
    const percent = clampPercent((payments / PSLF_REQUIRED_PAYMENTS) * 100);
    cards.push({
      id: 'pslf',
      title: 'Loan Forgiveness (PSLF)',
      icon: PhShieldCheck,
      linkLabel: 'View Progress',
      route: 'authenticated.pslf.index',
      value: `${percent}%`,
      caption: 'Loan Forgiveness Progress',
      percent,
      meta: `${payments}/${PSLF_REQUIRED_PAYMENTS} payments made`,
      metaIcon: PhTrendUp,
      source: model.pslfData.isLiveData ? 'live' : 'demo',
    });
  }

  return cards;
}

/**
 * Discover cards. Copy is lifted verbatim from the design.
 *
 * The Tuition Assistance card appears only in the new-user "Explore Your
 * Benefits" grid; once someone is active, TA is represented by its progress
 * card instead, and the design's "More to Explore" grid drops it. PSLF follows
 * the same rule from the moment a form is on file, so the benefit is never
 * offered for discovery beside its own progress card.
 */
function buildDiscover(input: BetaHomeInput, audience: BetaHomeAudience): BetaDiscoverCard[] {
  const { model, flags } = input;
  const cards: BetaDiscoverCard[] = [];
  const hasPslfProgress = model.pslfData.forms.length > 0;

  if (audience === 'new' && flags.showTuitionAssistance) {
    cards.push({
      id: 'tuition-assistance',
      title: 'Tuition Assistance',
      description: 'We’re invested in your education. Explore your options!',
      meta: `${money(model.section127Info.annualLimit)}/yr`,
      icon: PhGraduationCap,
      route: 'authenticated.tas.dashboard',
    });
  }

  if (flags.showPslf && !hasPslfProgress) {
    cards.push({
      id: 'pslf',
      title: 'Loan Forgiveness (PSLF)',
      description: 'Qualifying employment can unlock your loan forgiveness.',
      meta: 'up to 100% forgiven',
      icon: PhShieldCheck,
      route: 'authenticated.pslf.index',
    });
  }

  if (flags.showFinancialWellness) {
    cards.push({
      id: 'financial-wellness',
      title: 'Loan Wellness',
      description: 'See where your loans stand and what to do next.',
      meta: 'check your loan health',
      icon: PhFirstAidKit,
      route: 'authenticated.wellness-insights',
    });
  }

  if (flags.showLpn) {
    cards.push({
      id: 'lpn',
      title: 'Learning Partner Network',
      description: 'You may be eligible for discounts at top institutions!',
      meta: 'up to 25% off',
      icon: PhGlobe,
      route: 'authenticated.lpn.index',
    });
  }

  if (flags.showMentoring) {
    cards.push({
      id: 'mentorship',
      title: 'Education Mentorship',
      description: 'We’re here to help you maximize your benefits.',
      meta: 'make an appointment',
      icon: PhHeadset,
      route: 'authenticated.tas.mentoring',
    });
  }

  if (flags.showSyf) {
    cards.push({
      id: 'syf',
      title: 'Secure Your Future',
      description: 'Qualifying loan payments can trigger a 401(k) match.',
      /**
       * GAP (syf-match): the design hardcodes "$800/year". The real figure is
       * the person's employer 401(k) match cap, which no endpoint exposes on
       * this screen. Left generic rather than printing a fabricated dollar
       * amount.
       */
      meta: 'see your match',
      icon: PhRocketLaunch,
      route: 'authenticated.syf.dashboard',
    });
  }

  return cards;
}

/** Resources cards. No meta line, per the design. */
function buildResources(flags: BetaHomeFlags): BetaDiscoverCard[] {
  const cards: BetaDiscoverCard[] = [
    {
      id: 'faqs',
      title: 'FAQs',
      description: 'Have a question? We likely have the answers you’re looking for.',
      icon: PhSealQuestion,
      href: 'https://tio.zendesk.com/hc/en-us',
    },
    {
      id: 'strategy-finder',
      title: 'Strategy Finder',
      description: 'Compare options and find the best plan for your situation.',
      icon: PhTarget,
      route: 'authenticated.repaying-student-debt.repayment-strategy-finder',
    },
    {
      id: 'knowledge-base',
      title: 'Knowledge Base',
      description: 'Learn more about the benefit programs available to you.',
      icon: PhBookOpen,
      route: 'authenticated.questions-you-have.glossary',
    },
  ];

  if (flags.showIdr) {
    cards.push({
      id: 'idr',
      title: 'Income Driven Repayment',
      description: 'Balance your payments so you only pay what you can afford.',
      icon: PhScales,
      route: 'authenticated.idr.dashboard',
    });
  }

  return cards;
}

export function buildBetaHomeModel(input: BetaHomeInput): BetaHomeModel {
  const audience = audienceFor(input.model, input.flags);

  return {
    audience,
    greeting: greetingFor(input.hour),
    firstName: input.firstName,
    availableTuition: `${money(input.model.section127Info.annualLimit)}/yr`,
    availableTuitionSource: input.model.section127Info.isLiveData ? 'live' : 'demo',
    stats: buildStats(input),
    alerts: buildAlerts(input),
    progressCards: buildProgressCards(input),
    discover: buildDiscover(input, audience),
    resources: buildResources(input.flags),
  };
}

/** Re-exported so components and tests can assert the PSLF denominator. */
export { PSLF_REQUIRED_PAYMENTS };
